/**
 * Logger utility for handling environment-specific logging
 * - Production/Staging: Only errors are logged
 * - Development/Local: All log levels are enabled
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

interface LoggerConfig {
  environment: string;
  maxLogLevel: LogLevel;
  enableConsoleOutput: boolean;
  enableRemoteLogging?: boolean;
  remoteEndpoint?: string;
}

class Logger {
  private config: LoggerConfig;
  private isDevelopment: boolean;
  private isProduction: boolean;

  constructor() {
    const environment = process.env.REACT_APP_ENV || 'local';
    
    this.isDevelopment = ['dev', 'local'].includes(environment.toLowerCase());
    this.isProduction = ['prd', 'stg'].includes(environment.toLowerCase());
    
    this.config = {
      environment,
      maxLogLevel: this.isDevelopment ? LogLevel.DEBUG : LogLevel.ERROR,
      enableConsoleOutput: this.isDevelopment,
      enableRemoteLogging: false, // Disabled - no remote endpoint implementation yet
      remoteEndpoint: undefined // Can be configured later if needed
    };
  }

  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    return level <= this.config.maxLogLevel;
  }

  /**
   * Get timestamp for log entries
   */
  private getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Format log message with context
   */
  private formatMessage(level: LogLevel, message: string, ...args: any[]): string {
    const levelName = LogLevel[level];
    const timestamp = this.getTimestamp();
    const formattedArgs = args.length > 0 ? ` | ${args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ')}` : '';
    
    return `[${timestamp}] ${levelName}: ${message}${formattedArgs}`;
  }

  /**
   * Send logs to remote endpoint (for production monitoring)
   * Currently empty - no remote endpoint implementation
   */
  private async sendToRemote(level: LogLevel, message: string, ...args: any[]): Promise<void> {
    // TODO: Implement remote logging when endpoint is available
    return;
  }

  /**
   * Internal logging method
   */
  private logInternal(level: LogLevel, message: string, ...args: any[]): void {
    if (!this.shouldLog(level)) {
      return;
    }

    // Always send to remote if configured (for production monitoring)
    if (this.config.enableRemoteLogging) {
      this.sendToRemote(level, message, ...args).catch(() => {
        // Ignore remote logging failures
      });
    }

    // Console output based on environment
    if (this.config.enableConsoleOutput) {
      const formattedMessage = this.formatMessage(level, message, ...args);
      
      switch (level) {
        case LogLevel.ERROR:
          console.error(formattedMessage);
          break;
        case LogLevel.WARN:
          console.warn(formattedMessage);
          break;
        case LogLevel.INFO:
          console.info(formattedMessage);
          break;
        case LogLevel.DEBUG:
          console.log(formattedMessage);
          break;
      }
    } else {
      // In production, only show errors in console
      if (level === LogLevel.ERROR) {
        console.error(this.formatMessage(level, message, ...args));
      }
    }
  }

  /**
   * Log error messages - Always shown in all environments
   */
  error(message: string, ...args: any[]): void {
    this.logInternal(LogLevel.ERROR, message, ...args);
  }

  /**
   * Log warning messages - Shown in dev/local, suppressed in prd/stg
   */
  warn(message: string, ...args: any[]): void {
    this.logInternal(LogLevel.WARN, message, ...args);
  }

  /**
   * Log info messages - Only shown in dev/local
   */
  info(message: string, ...args: any[]): void {
    this.logInternal(LogLevel.INFO, message, ...args);
  }

  /**
   * Log debug messages - Only shown in dev/local
   */
  debug(message: string, ...args: any[]): void {
    this.logInternal(LogLevel.DEBUG, message, ...args);
  }

  /**
   * Convenience method for general logging (maps to info level)
   */
  log(message: string, ...args: any[]): void {
    this.info(message, ...args);
  }

  /**
   * Configure remote logging endpoint
   */
  setRemoteEndpoint(endpoint: string): void {
    this.config.remoteEndpoint = endpoint;
  }

  /**
   * Get current configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }

  /**
   * Check if running in development mode
   */
  isDev(): boolean {
    return this.isDevelopment;
  }

  /**
   * Check if running in production mode
   */
  isProd(): boolean {
    return this.isProduction;
  }

  /**
   * Create a prefixed logger for specific modules/components
   */
  createModuleLogger(moduleName: string): ModuleLogger {
    return new ModuleLogger(this, moduleName);
  }
}

/**
 * Module-specific logger that prefixes messages with module name
 */
class ModuleLogger {
  constructor(private parentLogger: Logger, private moduleName: string) {}

  private prefixMessage(message: string): string {
    return `[${this.moduleName}] ${message}`;
  }

  error(message: string, ...args: any[]): void {
    this.parentLogger.error(this.prefixMessage(message), ...args);
  }

  warn(message: string, ...args: any[]): void {
    this.parentLogger.warn(this.prefixMessage(message), ...args);
  }

  info(message: string, ...args: any[]): void {
    this.parentLogger.info(this.prefixMessage(message), ...args);
  }

  debug(message: string, ...args: any[]): void {
    this.parentLogger.debug(this.prefixMessage(message), ...args);
  }

  log(message: string, ...args: any[]): void {
    this.parentLogger.info(this.prefixMessage(message), ...args);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience methods for easy migration from console
export const log = {
  error: (message: string, ...args: any[]) => logger.error(message, ...args),
  warn: (message: string, ...args: any[]) => logger.warn(message, ...args),
  info: (message: string, ...args: any[]) => logger.info(message, ...args),
  debug: (message: string, ...args: any[]) => logger.debug(message, ...args),
  log: (message: string, ...args: any[]) => logger.info(message, ...args),
};

export default logger;
